//React lib
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

//Third party lib
import { Box, Button, Grid, Modal } from "@mui/material";
import { Chart as ChartJS, Title, Tooltip, Legend } from "chart.js";
import { CategoryScale, LinearScale } from "chart.js";
import { PointElement, LineElement } from "chart.js";
import { Line } from "react-chartjs-2";

//Icon
import ArrowDropDownSharpIcon from "@mui/icons-material/ArrowDropDownSharp";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";

import Label from "../../component/Label";
import Toaster from "../../component/Toaster";

import { dashboardAction } from "../../store/dashboard/dashboardSlice";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1100,
  bgcolor: "#fff",
  boxShadow: 24,
  border: "transparant",
  p: 4,
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const options = {
  responsive: true,
  plugins: {
    display: true,
    legend: {
      labels: {
        usePointStyle: true,
        pointStyle: "circle",
      },
      boxWidth: 6,
      position: "bottom" as const,
      padding: 20,
    },
  },
};

export default function ActiveStudentChart() {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();

  const [studentVsReceiptsData, setStudentVsReceiptsData] = useState<any>();
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(6);
  const [chartOpen, setChartOpen] = useState(false);
  const [modalMonthDropdownOpen, setModalMonthDropdownOpen] = useState(false); 
  const [mainMonthDropdownOpen, setMainMonthDropdownOpen] = useState(false);

  const months = studentVsReceiptsData
    ? Object.keys(studentVsReceiptsData).slice(0, selectedMonths)
    : [];

  const data = {
    labels: months,
    datasets: [
      {
        label: "Net students",
        borderColor: "#4CDFE8",
        backgroundColor: "#4CDFE8",
        data: months?.map(
          (month: any) => studentVsReceiptsData[month]["Active Students"]
        ),
      },
      {
        label: "No. of receipts raised",
        borderColor: "#FF8A34",
        backgroundColor: "#FF8A34",
        data: months?.map(
          (month: any) => studentVsReceiptsData[month]["Receipts"]
        ),
      },
    ],
  };

  useEffect(() => {
    netStudentVsReceipts();
  }, [selectedMonths]);

  const netStudentVsReceipts = () => {
    dispatch(
      dashboardAction({
        apiAction: "netStudentVsReceipts",
        months: selectedMonths,
      })
    )
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          setStudentVsReceiptsData(result?.payload?.data?.data);
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        // This console for debug the app if any error occurred in DEV
        console.log(err);
      });
  };

  const handleMonthDropdownOpen = () => {
    setMainMonthDropdownOpen(true);
  };

  const handleMonthSelect = (months: number) => {
    setSelectedMonths(months);
    setMainMonthDropdownOpen(false);
    setModalMonthDropdownOpen(false); 
  };

  const handleChartOpen = () => {
    setChartOpen(true);
    setModalMonthDropdownOpen(false); 
    setMainMonthDropdownOpen(false); 
  };

  const handleChartClose = () => {
    setChartOpen(false);
    setMainMonthDropdownOpen(false);
  };

  return (
    <div className="dashboard-active-students-container"
  onClick={() => {
    if (monthDropdownOpen) {
      setMonthDropdownOpen(false);
    } else if (mainMonthDropdownOpen) {
      setMainMonthDropdownOpen(false);
    }
  }}
>
      <div className="dashboard-active-students-title-row">
        <div className="dashboard-heading dashboard-active-students-title">
          <div className="font-14 bolder">{t("netStudentsVsReceipts")}</div>
        </div>
        <Grid>
          <div className="dashboard-active-students-dropdown">
            <Button
              variant="contained"
              className={`custom-dropdown-btn ${
                mainMonthDropdownOpen === true ? "sms-active-btn" : "sms-btn"
              }`}
              sx={{
                padding: "3px",
                height: "25px",
              }}
              onClick={handleMonthDropdownOpen}
            >
              {selectedMonths === 6 ? "Last 6 Month" : "Last 12 Month"}
              <ArrowDropDownSharpIcon />
            </Button>

            {mainMonthDropdownOpen === true ? (
              <div className="sms-box">
                <div
                  className="border-bottom padding-y-10 dropdown-month"
                  onClick={() => handleMonthSelect(6)}
                >
                  <Label title={t("last6month")} />
                </div>
                <div
                  className="padding-y-10 dropdown-month"
                  onClick={() => handleMonthSelect(12)}
                >
                  <Label title={t("last12month")} />
                </div>
              </div>
            ) : (
              ""
            )}
          </div>
        </Grid>
        <div className="open-in-full-btn">
          <span className="text-dark pointer" onClick={handleChartOpen}>
            <OpenInFullIcon />
          </span>
        </div>
      </div>

      <div className="dashboard-active-students-chart-container">
        <Line options={options} data={data} height={125} />
      </div>
      <Modal
        open={chartOpen}
        onClose={handleChartClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <div className="flex justify-content-between align-item-center">
            <div className=" dashboard-heading padding-y-10 ">
              <div className="font-14 bolder">
                {t("netStudentsVsReceipts")}
              </div>
            </div>
            <div className=" dashboard-heading padding-y-10 ">
              <Grid className="padding-x-10 ">
                <div>
                  <Button
                    variant="contained"
                    className={
                      modalMonthDropdownOpen === true ? "sms-active-btn" : "sms-btn"
                    }
                    onClick={() => setModalMonthDropdownOpen(true)}
                  >
                    {selectedMonths === 6 ? "Last 6 Month" : "Last 12 Month"}
                    <ArrowDropDownSharpIcon />
                  </Button>
                  <span
                    className="text-dark padding-x-10 pointer"
                    onClick={handleChartClose}
                  >
                    <CloseIcon />
                  </span>
                  {modalMonthDropdownOpen === true && (
                    <div className="sms-box">
                      <div
                        className="border-bottom padding-y-10 dropdown-month"
                        onClick={() => handleMonthSelect(6)}
                      >
                        <Label title={t("last6month")} />
                      </div>
                      <div
                        className="padding-y-10 dropdown-month"
                        onClick={() => handleMonthSelect(12)}
                      >
                        <Label title={t("last12month")} />
                      </div>
                    </div>
                  )}
                </div>
              </Grid>
            </div>
          </div>

          <Line options={options} data={data} height={125} />
        </Box>
      </Modal>
    </div>
  );
}
